import { type ReactNode, useState } from 'react'
import { Button, PaginationBar, Skeleton, Status, Table, Tooltip } from '@systemeio/components'
import { DotsHorizontalIcon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next/pages'
import Image from 'next/image'
import schoolEmptyImage from 'shared/assets/school-empty.png'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import { usePagination } from 'shared/route-query/hooks/use-pagination'
import type { DomainAssetsInterface } from 'shared/types/domain'
import { OrderEnum } from 'shared/types/swr-pagination-interface'
import { baseServerSideProps } from 'shared/utils/base-server-side-props'
import { formatDateTimeUtil } from 'shared/utils/date-time/format-date-time'
import { withDomainAssets } from 'shared/utils/with-domain-assets'
import { ProfileLayout } from 'modules/profile/components/profile-layout'
import { getProfilePageLayout } from 'modules/profile/layouts/get-profile-page-layout'
import CancelSubscriptionModal from 'modules/profile/manage-subscriptions/components/cancel-subscription-modal'
import { useManageSubscriptions } from 'modules/profile/manage-subscriptions/hooks/use-manage-subscriptions'
import type { ManageSubscriptionsInterface } from 'modules/profile/manage-subscriptions/types/manage-subscriptions-interface'

function ManageSubscriptions(): ReactNode {
  const { t } = useTranslation()
  const { user } = useUser()
  const authorizedUser = isAuthorizedUser(user) ? user : undefined
  const { limit, pagination, setPagination, setLimit } = usePagination({ isStaticUrl: true })
  const formatSubscriptionDate = (date: string): string | undefined =>
    formatDateTimeUtil(date, {
      dateFormat: authorizedUser?.dateFormat,
      timeFormat: authorizedUser?.timeFormat,
      timeZone: authorizedUser?.timezone,
    }).formatted

  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<number | null>(null)
  const renderStatus = (
    subscription: ManageSubscriptionsInterface,
    size: 'sm' | 'md',
  ): ReactNode => {
    if (user && !isAuthorizedUser(user)) return null

    const isActive = subscription.status === 'active'
    const label = t(
      isActive ? 'profile.subscriptions.status.active' : 'profile.subscriptions.status.cancelled',
    )
    const deactivatedAt = subscription.subscriptionItems[0].deactivatedAt
    const description =
      !isActive && deactivatedAt ? `${label} - ${formatSubscriptionDate(deactivatedAt)}` : label

    return (
      <Tooltip
        content={description}
        trigger={
          <Status size={size} color={isActive ? 'active' : 'inactive'}>
            {label}
          </Status>
        }
        triggerProps={{
          'aria-label': description,
          className: 'inline-flex max-w-full rounded-full',
        }}
      />
    )
  }
  const renderCancel = (subscription: ManageSubscriptionsInterface): ReactNode =>
    subscription.status === 'active' ? (
      <Tooltip
        content={t('profile.subscriptions.cancel_subscription')}
        trigger={<DotsHorizontalIcon aria-hidden="true" className="size-5" />}
        triggerProps={{
          render: (
            <Button
              variant="tertiary-gray"
              size="icon-sm"
              aria-label={t('profile.subscriptions.cancel_subscription')}
              onClick={() => setSelectedSubscriptionId(subscription.id)}
            />
          ),
        }}
      />
    ) : null

  const { data: subscriptions } = useManageSubscriptions({
    limit,
    pagination,
  })

  const selectedSubscription = subscriptions?.items.find(
    subscription => subscription.id === selectedSubscriptionId,
  )

  return (
    <ProfileLayout title={t('profile.user_settings.manage_subscriptions.title')}>
      <div className="flex h-fit w-full min-w-0 flex-col gap-4">
        {subscriptions?.items.length === 0 ? (
          <div className="flex min-h-96 flex-col items-center justify-center gap-4 rounded-xl border border-border-card bg-bg-primary px-4 desktop:min-h-173">
            <div className="relative h-32.5 w-31.5">
              <Image
                src={schoolEmptyImage}
                alt=""
                width={130}
                height={134}
                className="absolute -start-0.5 -top-0.5 max-w-none"
              />
            </div>
            <div className="flex flex-col items-center gap-2 text-center text-base leading-5">
              <h2 className="text-base font-normal text-text-primary">
                {t('profile.subscriptions.empty.title')}
              </h2>
              <p className="text-text-secondary">{t('profile.subscriptions.empty.description')}</p>
            </div>
          </div>
        ) : (
          <div className="flex w-full min-w-0 flex-col gap-3">
            <div
              className="hidden overflow-hidden rounded-xl border border-border-card bg-bg-primary tablet:block desktop:rounded-none [&_tbody_tr]:h-17 [&_td]:px-4 [&_td]:py-3.5 [&_td]:text-base [&_td:last-child]:px-3.5 desktop:[&_td:last-child]:px-4 [&_th]:px-4 [&_th]:text-sm [&_th]:normal-case"
              data-test-table-is-loaded={!!subscriptions}
            >
              <Table.Root
                aria-label={t('profile.user_settings.manage_subscriptions.title')}
                className="table-fixed"
              >
                <Table.Header>
                  <Table.Head>{t('profile.subscriptions.title_plural')}</Table.Head>
                  <Table.Head className="w-30 xl:w-75">
                    {t('profile.subscriptions.status.title')}
                  </Table.Head>
                  <Table.Head className="xl:w-64">{t('profile.subscriptions.date')}</Table.Head>
                  <Table.Head className="w-15 xl:w-22">
                    <span className="sr-only">
                      {t('profile.subscriptions.cancel_subscription')}
                    </span>
                  </Table.Head>
                </Table.Header>
                <Table.Body>
                  {subscriptions
                    ? subscriptions.items.map(subscription => (
                        <Table.Row key={subscription.id} id={subscription.id}>
                          <Table.Cell truncate={false}>
                            <span className="block min-h-10 content-center break-words">
                              {subscription.subscriptionItems[0].orderItem.pricePlan.innerName}
                            </span>
                          </Table.Cell>
                          <Table.Cell>{renderStatus(subscription, 'md')}</Table.Cell>
                          <Table.Cell truncate={false}>
                            {(!user || isAuthorizedUser(user)) &&
                              formatSubscriptionDate(subscription.createdAt)}
                          </Table.Cell>
                          <Table.Cell>
                            <div className="flex justify-end">{renderCancel(subscription)}</div>
                          </Table.Cell>
                        </Table.Row>
                      ))
                    : Array.from({ length: 10 }, (_, index) => (
                        <Table.Row key={index}>
                          <Table.LoaderCell />
                          <Table.LoaderCell />
                          <Table.LoaderCell />
                          <Table.LoaderCell />
                        </Table.Row>
                      ))}
                </Table.Body>
              </Table.Root>
            </div>
            <div
              className="flex flex-col gap-2.5 tablet:hidden"
              data-test-table-is-loaded={!!subscriptions}
            >
              {subscriptions
                ? subscriptions.items.map(subscription => (
                    <div
                      key={subscription.id}
                      className="flex min-w-0 items-center rounded-xl border border-border-card bg-bg-primary py-3"
                    >
                      <div className="flex min-w-0 flex-1 flex-col gap-1 px-4">
                        <span className="text-base leading-5 break-words text-text-secondary">
                          {subscription.subscriptionItems[0].orderItem.pricePlan.innerName}
                        </span>
                        <span className="text-base text-text-secondary">
                          {(!user || isAuthorizedUser(user)) &&
                            formatSubscriptionDate(subscription.createdAt)}
                        </span>
                      </div>
                      <div className="shrink-0">{renderStatus(subscription, 'sm')}</div>
                      <div className="flex w-16 shrink-0 justify-center">
                        {renderCancel(subscription)}
                      </div>
                    </div>
                  ))
                : Array.from({ length: 10 }, (_, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-4 rounded-xl border border-border-card bg-bg-primary p-4"
                    >
                      <div className="flex min-w-0 flex-1 flex-col gap-1">
                        <Skeleton className="h-5 w-full" />
                        <Skeleton className="h-4 w-3/4" />
                      </div>
                      <Skeleton className="h-6 w-16" />
                      <Skeleton className="size-8" />
                    </div>
                  ))}
            </div>
            <div className="flex items-center justify-end gap-5 sm:gap-10">
              {subscriptions &&
                ((pagination.order !== 'prev' && subscriptions.hasMore) ||
                  pagination.order === 'prev' ||
                  pagination.startFromId !== undefined ||
                  limit !== 10) && (
                  <PaginationBar
                    limit={limit}
                    hasNextPage={
                      !!subscriptions &&
                      subscriptions.items.length > 0 &&
                      (subscriptions.hasMore || pagination.order === 'prev')
                    }
                    hasPreviousPage={
                      !!subscriptions &&
                      subscriptions.items.length > 0 &&
                      pagination.startFromId !== undefined &&
                      !(pagination.order === 'prev' && !subscriptions?.hasMore)
                    }
                    onNextPage={() =>
                      setPagination(prev => ({
                        ...prev,
                        startFromId: subscriptions?.items.at(-1)?.id,
                        order: OrderEnum.next,
                      }))
                    }
                    onPreviousPage={() =>
                      setPagination(prev => ({
                        ...prev,
                        startFromId: subscriptions?.items[0]?.id,
                        order: OrderEnum.prev,
                      }))
                    }
                    onLimitChange={nextLimit => {
                      setLimit(nextLimit)
                      setPagination(prev => ({
                        ...prev,
                        startFromId: undefined,
                        order: OrderEnum.next,
                      }))
                    }}
                  />
                )}
            </div>
          </div>
        )}
        {selectedSubscription && (
          <CancelSubscriptionModal
            subscription={selectedSubscription}
            opened
            onClose={() => setSelectedSubscriptionId(null)}
          />
        )}
      </div>
    </ProfileLayout>
  )
}

ManageSubscriptions.getLayout = (
  page: ReactNode,
  props: { domainAssets?: DomainAssetsInterface },
) =>
  getProfilePageLayout(page, {
    titleKey: 'profile.user_settings.manage_subscriptions.title',
    domainAssets: props.domainAssets,
  })

export const getServerSideProps = withDomainAssets(baseServerSideProps)

export default ManageSubscriptions
